import React from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Paper,
  Typography
} from "@material-ui/core";
import RefreshIcon from "@material-ui/icons/Refresh";
import WarningIcon from "@material-ui/icons/Warning";
import { createMuiTheme, MuiThemeProvider } from "@material-ui/core/styles";
import MUIDataTable from "mui-datatables";
import axios from "axios";
import dateFormat from "dateformat";
import { Container, Row, Col } from "shards-react";
import api from "../common/api";
import Chart from "../../utils/chart";
import colors from "../../utils/colors";

const formatMoney = value => {
  if (value === null || value === undefined || value === "") return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return "—";
  return num.toLocaleString("es-AR", {
    style: "currency",
    currency: "ARS",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });
};

const formatPercent = value => {
  if (value === null || value === undefined || value === "") return null;
  const num = Number(value);
  if (Number.isNaN(num)) return null;
  return num;
};

const clientLabel = row => {
  const name = (row.business_name || "").trim();
  if (name) return name;
  return row.client_id ? `Cliente #${row.client_id}` : "—";
};

const usageBarColor = (pct, exceeded) => {
  if (exceeded) return "#c4183c";
  if (pct >= 90) return "#f5a623";
  if (pct >= 75) return "#674eec";
  return "#17c671";
};

const UsageBar = ({ percentage, exceeded, limit }) => {
  const pct = formatPercent(percentage);
  const limitNum = Number(limit) || 0;

  if (limitNum <= 0) {
    return (
      <Typography variant="caption" color="textSecondary">
        Sin límite
      </Typography>
    );
  }

  if (pct === null) {
    return (
      <Typography variant="caption" color="textSecondary">
        —
      </Typography>
    );
  }

  const displayPct = Math.min(Math.max(pct, 0), 100);
  const fillColor = usageBarColor(displayPct, exceeded);

  return (
    <Box minWidth={120} maxWidth={160}>
      <Box display="flex" justifyContent="space-between" mb={0.5}>
        <Typography
          variant="caption"
          style={{
            fontWeight: exceeded ? 600 : 500,
            color: exceeded ? "#c4183c" : "inherit"
          }}
        >
          {displayPct.toFixed(1)}%
        </Typography>
        {exceeded && (
          <WarningIcon style={{ fontSize: 14, color: "#c4183c" }} />
        )}
      </Box>
      <Box
        height={8}
        borderRadius={4}
        bgcolor="#e9ecef"
        overflow="hidden"
      >
        <Box
          height="100%"
          width={`${displayPct}%`}
          borderRadius={4}
          bgcolor={fillColor}
          style={{ transition: "width 0.2s ease" }}
        />
      </Box>
    </Box>
  );
};

const MoneyCell = ({ value, highlight }) => (
  <Typography
    variant="body2"
    align="right"
    style={{
      fontVariantNumeric: "tabular-nums",
      fontWeight: highlight ? 600 : 400,
      color: highlight ? "#c4183c" : "inherit",
      whiteSpace: "nowrap"
    }}
  >
    {formatMoney(value)}
  </Typography>
);

const CreditLimitChart = ({ label, limit, available, percentage, exceeded }) => {
  const canvasRef = React.useRef(null);
  const chartRef = React.useRef(null);

  React.useEffect(() => {
    if (!canvasRef.current) return;

    const limitNum = Number(limit) || 0;
    if (limitNum <= 0) return;

    const availableNum = Math.max(0, Number(available) || 0);
    const used = Math.max(0, limitNum - availableNum);
    const chartColor = exceeded
      ? colors.salmon.toRGBA(0.85)
      : colors.primary.toRGBA(0.85);
    const availableColor = colors.success.toRGBA(0.75);

    chartRef.current = new Chart(canvasRef.current, {
      type: "doughnut",
      data: {
        labels: ["Utilizado", "Disponible"],
        datasets: [
          {
            data: [used, availableNum],
            backgroundColor: [chartColor, availableColor],
            borderWidth: 0
          }
        ]
      },
      options: {
        legend: { position: "bottom" },
        cutoutPercentage: 65,
        tooltips: {
          callbacks: {
            label: (tooltipItem, data) => {
              const dataset = data.datasets[tooltipItem.datasetIndex];
              const value = dataset.data[tooltipItem.index];
              return `${data.labels[tooltipItem.index]}: ${formatMoney(value)}`;
            }
          }
        }
      }
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [label, limit, available, percentage, exceeded]);

  const limitNum = Number(limit) || 0;
  if (limitNum <= 0) {
    return (
      <Typography variant="body2" color="textSecondary" align="center">
        Sin límite configurado
      </Typography>
    );
  }

  const pct = formatPercent(percentage);

  return (
    <Box textAlign="center" py={1}>
      <Typography variant="subtitle2" gutterBottom>
        {label}
      </Typography>
      <Typography variant="caption" color="textSecondary" display="block">
        Límite {formatMoney(limit)}
        {pct !== null ? ` · ${pct.toFixed(1)}% utilizado` : ""}
      </Typography>
      <canvas ref={canvasRef} height={140} />
      <Typography variant="body2">Disponible: {formatMoney(available)}</Typography>
      {exceeded && (
        <Chip size="small" color="secondary" label="Límite excedido" className="mt-1" />
      )}
    </Box>
  );
};

const tableTheme = createMuiTheme({
  overrides: {
    MuiTableCell: {
      root: {
        padding: "10px 16px",
        fontSize: "0.8125rem",
        borderBottom: "1px solid #e9ecef"
      },
      head: {
        color: "#3d5170",
        backgroundColor: "#f8f9fa"
      }
    },
    MUIDataTableHeadCell: {
      root: {
        padding: "12px 16px"
      }
    },
    MuiTableRow: {
      root: {
        "&:hover": {
          backgroundColor: "rgba(103, 78, 236, 0.04)"
        }
      }
    }
  }
});

class ClientExposures extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      data: [],
      isLoading: false,
      isRefreshing: false,
      detailOpen: false,
      detailRefreshConfirm: false,
      refreshError: null,
      selectedRow: null
    };
  }

  componentDidMount() {
    this.fetchExposures();
  }

  fetchExposures = () => {
    this.setState({ isLoading: true });
    const token = localStorage.getItem("userToken");

    return axios
      .get(api.clients().clientExposures(), {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(res => {
        const raw = Array.isArray(res.data) ? res.data : res.data.data || [];
        this.setState({
          data: raw,
          isLoading: false
        });
        return raw;
      })
      .catch(err => {
        console.log(err);
        this.setState({ isLoading: false, data: [] });
      });
  };

  handleRowClick = (rowData, rowMeta) => {
    const row = this.state.data[rowMeta.dataIndex];
    if (row) {
      this.setState({ selectedRow: row, detailOpen: true });
    }
  };

  handleDetailClose = () => {
    this.setState({
      detailOpen: false,
      selectedRow: null,
      detailRefreshConfirm: false,
      refreshError: null
    });
  };

  refreshClientExposures = clientIds => {
    const token = localStorage.getItem("userToken");
    this.setState({ isRefreshing: true, refreshError: null });

    return axios
      .post(
        api.clients().refreshExposures(),
        { clientIds },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then(() => this.fetchExposures())
      .finally(() => {
        this.setState({ isRefreshing: false });
      });
  };

  handleDetailRefreshRequest = () => {
    this.setState({ detailRefreshConfirm: true, refreshError: null });
  };

  handleDetailRefreshCancel = () => {
    if (!this.state.isRefreshing) {
      this.setState({ detailRefreshConfirm: false, refreshError: null });
    }
  };

  handleDetailRefreshConfirm = () => {
    const clientId = this.state.selectedRow && this.state.selectedRow.client_id;
    if (!clientId) return;

    this.refreshClientExposures([clientId])
      .then(rows => {
        const updated =
          rows && rows.find(r => r.client_id === clientId);
        this.setState({
          selectedRow: updated || this.state.selectedRow,
          detailRefreshConfirm: false,
          refreshError: null
        });
      })
      .catch(err => {
        const resData =
          err.response && err.response.data ? err.response.data : {};
        const message =
          resData.message ||
          resData.error ||
          "No se pudo actualizar el estado de cuenta.";
        this.setState({ refreshError: message });
      });
  };

  renderLimitColumn = (row, which) => {
    const limit = isFirst ? row.first_credit_limit : row.second_credit_limit;
    const isFirst = which === 1;
    const pct = isFirst
      ? row.percentage_of_first_credit_limit
      : row.percentage_of_second_credit_limit;
    const exceeded = isFirst
      ? row.has_exceeded_first_limit
      : row.has_exceeded_second_limit;
    const available = isFirst
      ? row.available_first_credit
      : row.available_second_credit;

    return (
      <Box py={0.5}>
        <UsageBar
          percentage={pct}
          exceeded={exceeded}
          limit={limit}
        />
        <Typography
          variant="caption"
          color="textSecondary"
          display="block"
          style={{ marginTop: 4 }}
        >
          Disp. {formatMoney(available)}
        </Typography>
      </Box>
    );
  };

  renderDetailField = (label, value, highlight) => (
    <Grid item xs={12} sm={2} key={label}>
      <Typography variant="caption" color="textSecondary">
        {label}
      </Typography>
      <Typography
        variant="body1"
        style={highlight ? { fontWeight: 600, color: "#c4183c" } : {}}
      >
        {value}
      </Typography>
    </Grid>
  );


  renderDetailModal() {
    const {
      selectedRow: row,
      detailOpen,
      detailRefreshConfirm,
      isRefreshing,
      refreshError
    } = this.state;
    if (!row) return null;

    const exceeded =
      row.has_exceeded_first_limit || row.has_exceeded_second_limit;

    return (
      <Dialog
        open={detailOpen}
        onClose={this.handleDetailClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle disableTypography>
          <Typography variant="h6">{clientLabel(row)}</Typography>
          <Typography variant="body2" color="textSecondary">
            {row.sap_code ? `SAP ${row.sap_code}` : "Sin código SAP"}
            {row.updated_at &&
              ` · Actualizado ${dateFormat(row.updated_at, "dd/mm/yyyy HH:MM")}`}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12} md={12}>
              <Typography variant="overline" color="textSecondary">
                Saldos y exposición
              </Typography>
              <Grid container spacing={2} className="mb-3">
                {this.renderDetailField("Saldo SAP", formatMoney(row.sap_balance))}
                {this.renderDetailField(
                  "OTs pendientes",
                  formatMoney(row.pending_work_orders)
                )}
                {this.renderDetailField(
                  "Exposición total",
                  formatMoney(row.total_exposure),
                  exceeded
                )}
              </Grid>
              <div className="w-full d-flex flex-row justify-content-between">
                <div className="w-1/2">
                  <CreditLimitChart
                    label="Primer límite"
                    limit={row.first_credit_limit}
                    available={row.available_first_credit}
                    percentage={row.percentage_of_first_credit_limit}
                    exceeded={row.has_exceeded_first_limit}
                  />
                </div>
                <div className="w-1/2">
                  <CreditLimitChart
                    label="Segundo límite"
                    limit={row.second_credit_limit}
                    available={row.available_second_credit}
                    percentage={row.percentage_of_second_credit_limit}
                    exceeded={row.has_exceeded_second_limit}
                  />
                </div>
              </div>

              <Typography variant="overline" color="textSecondary">
                Límites de crédito
              </Typography>
              <Grid container spacing={2}>
                {this.renderDetailField(
                  "Primer límite",
                  formatMoney(row.first_credit_limit)
                )}
                {this.renderDetailField(
                  "Disponible 1er límite",
                  formatMoney(row.available_first_credit)
                )}
                {this.renderDetailField(
                  "Utilización 1er límite",
                  formatPercent(row.percentage_of_first_credit_limit) !== null
                    ? `${formatPercent(row.percentage_of_first_credit_limit).toFixed(1)}%`
                    : "—",
                  row.has_exceeded_first_limit
                )}
                {this.renderDetailField(
                  "Segundo límite",
                  formatMoney(row.second_credit_limit)
                )}
                {this.renderDetailField(
                  "Disponible 2do límite",
                  formatMoney(row.available_second_credit)
                )}
                {this.renderDetailField(
                  "Utilización 2do límite",
                  formatPercent(row.percentage_of_second_credit_limit) !== null
                    ? `${formatPercent(row.percentage_of_second_credit_limit).toFixed(1)}%`
                    : "—",
                  row.has_exceeded_second_limit
                )}
              </Grid>
            </Grid>

          </Grid>
          {detailRefreshConfirm && (
            <DialogContentText className="mt-2">
              Se recalcularán saldos y exposición de{" "}
              <strong>{clientLabel(row)}</strong> desde SAP y órdenes pendientes.
              ¿Desea continuar?
            </DialogContentText>
          )}
          {refreshError && (
            <Typography variant="body2" color="error" className="px-3 pb-2">
              {refreshError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          {detailRefreshConfirm ? (
            <>
              <Button
                onClick={this.handleDetailRefreshCancel}
                disabled={isRefreshing}
              >
                Cancelar
              </Button>
              <Button
                onClick={this.handleDetailRefreshConfirm}
                color="primary"
                variant="contained"
                disabled={isRefreshing || !row.client_id}
                startIcon={
                  isRefreshing ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <RefreshIcon />
                  )
                }
              >
                {isRefreshing ? "Actualizando…" : "Confirmar"}
              </Button>
            </>
          ) : (
            <>
              {row.client_id && (
                <Button
                  color="primary"
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={this.handleDetailRefreshRequest}
                  disabled={isRefreshing}
                  style={{ fontSize: '0.75rem' }}
                >
                  Actualizar estado de cuenta
                </Button>
              )}
              {row.client_id && (
                <Button
                  color="primary"
                  variant="outlined"
                  href={`/client/${row.client_id}/1`}
                  style={{ fontSize: '0.75rem' }}
                >
                  Ver cliente
                </Button>
              )}
              <Button onClick={this.handleDetailClose} color="primary" style={{ fontSize: '0.75rem' }}>
                Cerrar
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    );
  }

  render() {
    const { data, isLoading, selectedRow, detailOpen } = this.state;

    const moneyColumnProps = {
      setCellHeaderProps: () => ({ style: { textAlign: "right" } }),
      setCellProps: () => ({ style: { textAlign: "right" } })
    };

    const columns = [
      {
        name: "client_id",
        label: "ID",
        options: {
          filter: false,
          sort: true,
          display: false
        }
      },
      {
        name: "business_name",
        label: "Cliente",
        options: {
          filter: true,
          sort: true,
          customBodyRender: (value, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            if (!row) return "—";
            const sap = (row.sap_code || "").trim();
            return (
              <Box>
                <Typography variant="body2" style={{ fontWeight: 500 }}>
                  {clientLabel(row)}
                </Typography>
                {sap ? (
                  <Typography variant="caption" color="textSecondary">
                    SAP {sap}
                  </Typography>
                ) : (
                  <Typography variant="caption" color="textSecondary">
                    ID {row.client_id}
                  </Typography>
                )}
              </Box>
            );
          }
        }
      },
      {
        name: "sap_code",
        label: "Cód. SAP",
        options: { display: false, filter: false }
      },
      {
        name: "sap_balance",
        label: "Saldo SAP",
        options: {
          ...moneyColumnProps,
          filter: false,
          sort: true,
          customBodyRender: value => <MoneyCell value={value} />
        }
      },
      {
        name: "pending_work_orders",
        label: "OTs pend.",
        options: {
          ...moneyColumnProps,
          filter: false,
          sort: true,
          customBodyRender: value => <MoneyCell value={value} />
        }
      },
      {
        name: "total_exposure",
        label: "Exposición",
        options: {
          ...moneyColumnProps,
          filter: false,
          sort: true,
          customBodyRender: (value, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            const highlight =
              row &&
              (row.first_credit_limit > 0 || row.second_credit_limit > 0) &&
              (row.has_exceeded_first_limit || row.has_exceeded_second_limit);
            return <MoneyCell value={value} highlight={highlight} />;
          }
        }
      },
      {
        name: "first_credit_limit",
        label: "1er límite",
        options: { display: false, filter: false }
      },
      {
        name: "second_credit_limit",
        label: "2do límite",
        options: { display: false, filter: false }
      },
      {
        name: "available_first_credit",
        label: "Disp. 1er",
        options: { display: false, filter: false }
      },
      {
        name: "available_second_credit",
        label: "Disp. 2do",
        options: { display: false, filter: false }
      },
      {
        name: "percentage_of_first_credit_limit",
        label: "1er límite",
        options: {
          filter: false,
          sort: true,
          customBodyRender: (value, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            return row ? this.renderLimitColumn(row, 1) : "—";
          }
        }
      },
      {
        name: "has_exceeded_first_limit",
        label: "Excede 1er",
        options: {
          display: false,
          filter: true,
          filterType: "dropdown",
          filterOptions: {
            names: ["Si", "No"],
            logic: (value, filters) => {
              if (!filters.length) return true;
              return filters[0] === "Si" ? Boolean(value) : !value;
            }
          }
        }
      },
      {
        name: "percentage_of_second_credit_limit",
        label: "2do límite",
        options: {
          filter: false,
          sort: true,
          customBodyRender: (value, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            return row ? this.renderLimitColumn(row, 2) : "—";
          }
        }
      },
      {
        name: "has_exceeded_second_limit",
        label: "Excede 2do",
        options: {
          display: false,
          filter: true,
          filterType: "dropdown",
          filterOptions: {
            names: ["Si", "No"],
            logic: (value, filters) => {
              if (!filters.length) return true;
              return filters[0] === "Si" ? Boolean(value) : !value;
            }
          }
        }
      },
      {
        name: "updated_at",
        label: "Actualizado",
        options: {
          filter: false,
          sort: true,
          display: false,
          customBodyRender: value =>
            value ? dateFormat(value, "dd/mm/yyyy HH:MM") : "—"
        }
      }
    ];

    const options = {
      filter: false,
      search: true,
      selectableRows: false,
      responsive: "standard",
      download: false,
      viewColumns: false,
      print: false,
      rowsPerPage: 25,
      rowsPerPageOptions: [10, 25, 50, 100],
      onRowClick: this.handleRowClick,
      setTableProps: () => ({
        style: { cursor: "pointer" }
      }),
      setRowProps: (row, dataIndex) => {
        const item = data[dataIndex];
        const style = { cursor: "pointer" };
        if (
          detailOpen &&
          selectedRow &&
          item &&
          item.client_id === selectedRow.client_id
        ) {
          style.backgroundColor = "rgba(103, 78, 236, 0.08)";
        } else if (
          item &&
          (item.first_credit_limit > 0 || item.second_credit_limit > 0) &&
          (item.has_exceeded_first_limit || item.has_exceeded_second_limit)
        ) {
          style.backgroundColor = "rgba(196, 24, 60, 0.04)";
        }
        return { style };
      },
      customToolbarSelect: () => null,
      textLabels: {
        body: {
          noMatch: isLoading ? <CircularProgress size={28} /> : "No hay resultados",
          toolTip: "Ordenar"
        },
        toolbar: {
          search: "Buscar",
          downloadCsv: "Exportar CSV",
          viewColumns: "Columnas",
          filterTable: "Filtros"
        },
        pagination: {
          next: "Siguiente",
          previous: "Anterior",
          rowsPerPage: "Filas por página:",
          displayRows: "de"
        }
      }
    };

    return (
      <Container fluid className="main-content-container px-4">
        <Row>
          <Col className="p-2">
            <Box className="mb-2">
              <Typography variant="h5">Estados de cuenta</Typography>
              <Typography variant="body2" color="textSecondary">
                Hacé clic en una fila para ver el detalle y actualizar su estado
                de cuenta.
              </Typography>
            </Box>

            {this.renderDetailModal()}

            <Paper elevation={1}>
              <MuiThemeProvider theme={tableTheme}>
                <MUIDataTable
                  title={
                    <Box display="flex" alignItems="center">
                      {isLoading && (
                        <CircularProgress size={20} style={{ marginLeft: 12 }} />
                      )}
                    </Box>
                  }
                  data={data}
                  columns={columns}
                  options={options}
                />
              </MuiThemeProvider>
            </Paper>
          </Col>
        </Row>
      </Container>
    );
  }
}

export default ClientExposures;
